const MAX_PROPERTIES_PER_ITEM = 32;
const MAX_PROPERTY_NAME_LENGTH = 48;
const MAX_PROPERTY_TEXT_LENGTH = 500;

export type ProjectItemPropertyType = 'text' | 'number' | 'date' | 'status' | 'url' | 'checkbox';
export type ProjectItemPropertyValue = { type: ProjectItemPropertyType; value: string | number | boolean };
export type ProjectItemProperties = Record<string, ProjectItemPropertyValue>;
export type ProjectItemPropertyQuery = {
  name: string;
  operator?: 'equals' | 'contains' | 'before' | 'after' | 'truthy';
  value?: string | number | boolean;
};

export function normalizeProjectItemProperties(input: unknown): ProjectItemProperties {
  if (!input || typeof input !== 'object' || Array.isArray(input)) return {};
  const result: ProjectItemProperties = {};
  for (const [rawName, rawValue] of Object.entries(input as Record<string, unknown>)) {
    if (Object.keys(result).length >= MAX_PROPERTIES_PER_ITEM) break;
    const name = rawName.trim().replace(/\s+/gu, ' ').slice(0, MAX_PROPERTY_NAME_LENGTH);
    if (!name || !rawValue || typeof rawValue !== 'object' || Array.isArray(rawValue)) continue;
    const candidate = rawValue as Record<string, unknown>;
    const type = candidate.type;
    if (!['text','number','date','status','url','checkbox'].includes(String(type))) continue;
    if (type === 'checkbox') { result[name] = { type, value: candidate.value === true }; continue; }
    if (type === 'number') {
      const value = Number(candidate.value);
      if (Number.isFinite(value)) result[name] = { type, value };
      continue;
    }
    const value = String(candidate.value ?? '').trim().slice(0, MAX_PROPERTY_TEXT_LENGTH);
    if (!value) continue;
    if (type === 'url') {
      try {
        const url = new URL(value);
        if (url.protocol !== 'http:' && url.protocol !== 'https:') continue;
        result[name] = { type, value: url.toString() };
      } catch { continue; }
      continue;
    }
    if (type === 'date' && !/^\d{4}-\d{2}-\d{2}(?:T.*)?$/u.test(value)) continue;
    result[name] = { type: type as ProjectItemPropertyType, value };
  }
  return result;
}

export function projectItemMatchesPropertyQuery(
  entry: { properties: ProjectItemProperties },
  query: ProjectItemPropertyQuery,
): boolean {
  const property = Object.entries(entry.properties).find(([name]) => name.toLocaleLowerCase() === query.name.trim().toLocaleLowerCase())?.[1];
  if (!property) return false;
  const operator = query.operator ?? 'equals';
  if (operator === 'truthy') return property.value === true || (typeof property.value === 'string' && property.value.length > 0) || (typeof property.value === 'number' && property.value !== 0);
  if (operator === 'contains') return String(property.value).toLocaleLowerCase().includes(String(query.value ?? '').toLocaleLowerCase());
  if (operator === 'before' || operator === 'after') {
    const left = Date.parse(String(property.value));
    const right = Date.parse(String(query.value ?? ''));
    return Number.isFinite(left) && Number.isFinite(right) && (operator === 'before' ? left < right : left > right);
  }
  return property.value === query.value || String(property.value).toLocaleLowerCase() === String(query.value ?? '').toLocaleLowerCase();
}
